import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Stack,
  Button,
  Tooltip,
  MenuItem,
  TextField,
  Container,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
  LinearProgress,
  TablePagination,
} from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
import useColumnVisibility, { ColumnDef } from 'src/hooks/useColumnVisibility';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { ColumnsButton } from 'src/components/table';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// utils
import axiosInstance from '../../utils/axios';
import { APIS } from '../../utils/serviceUrls';
import { fDateTimeSuffix } from '../../utils/formatTime';
import fmtDuration from '../../utils/formatDuration';

// ----------------------------------------------------------------------
// Admin view of graded quiz attempts. Server-paginated over
// GET /v1/quiz-attempts ({total,page,pageSize,items}).
//
// `scope` makes this reusable as two report tabs:
//   undefined  → every quiz attempt (courses + programs)
//   'course'   → course quizzes only
//   'program'  → program/event quizzes only (per-lecture program quizzes)
// The backend returns sourceType/sourceName so one table labels both kinds.

type Parent = { _id?: string; title?: string; name?: string } | null;

type AttemptRow = {
  _id: string;
  user?: { _id?: string; name?: string; email?: string } | null;
  userId?: { _id?: string; name?: string; email?: string } | null;
  course?: Parent;
  courseId?: Parent;
  event?: Parent;
  eventId?: Parent;
  quizTitle?: string;
  quizId?: string;
  score?: number;
  maxScore?: number;
  percentage?: number;
  passed?: boolean;
  attemptNumber?: number;
  durationSeconds?: number;
  createdAt?: string;
  submittedAt?: string;
};

type Scope = 'course' | 'program' | undefined;

const PASS_FILTERS = [
  { value: '', label: 'All results' },
  { value: 'true', label: 'Passed' },
  { value: 'false', label: 'Failed' },
];

function safeDate(value?: string): string {
  if (!value) return '—';
  try {
    return fDateTimeSuffix(new Date(value));
  } catch {
    return value;
  }
}

const nameOf = (p: Parent) => p?.title || p?.name || '';

// One compact KPI tile for the summary strip.
function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <Card sx={{ p: 2.5, flex: 1, minWidth: 140 }}>
      <Typography variant="h4">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Card>
  );
}

export default function QuizResults({
  scope,
  eventId: fixedEventId,
  batchId,
  embedded,
}: {
  scope?: Scope;
  // When set, the report is locked to this program (picker hidden) — used by
  // the dedicated per-batch report page.
  eventId?: string;
  batchId?: string;
  // Render bare (no Page/breadcrumbs/Container) for embedding in another page.
  embedded?: boolean;
}) {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const isProgram = scope === 'program';
  const locked = !!fixedEventId;
  const heading = isProgram ? 'Program Quiz Results' : 'Quiz Results';
  // Column label for the parent the quiz belongs to.
  const sourceLabel = isProgram ? 'Program' : scope === 'course' ? 'Course' : 'Source';

  const [rows, setRows] = useState<AttemptRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [passed, setPassed] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any[]>([]);
  const [byStudent, setByStudent] = useState<any[]>([]);
  // Program picker (program scope only).
  const [programs, setPrograms] = useState<any[]>([]);
  const [eventId, setEventId] = useState('');

  const columns: ColumnDef[] = useMemo(
    () => [
      { id: 'date', label: 'Date' },
      { id: 'student', label: 'Student' },
      { id: 'source', label: sourceLabel },
      { id: 'quiz', label: 'Quiz' },
      { id: 'score', label: 'Score', align: 'center' },
      { id: 'percentage', label: '%', align: 'center' },
      { id: 'result', label: 'Result', align: 'center' },
      { id: 'attempt', label: 'Attempt', align: 'center' },
    ],
    [sourceLabel]
  );

  const { visibleColumns, toggleColumn, isVisible } = useColumnVisibility(
    columns,
    `kayfa.quizResults.${scope || 'all'}.visibleColumns`
  );

  // Shared query params for all three endpoints, so every panel stays in scope.
  // A fixed eventId/batchId (batch report page) wins over the local picker.
  const scopeParams = useMemo(
    () => ({
      scope: scope || undefined,
      eventId: fixedEventId || eventId || undefined,
      batchId: batchId || undefined,
    }),
    [scope, fixedEventId, eventId, batchId]
  );

  // Load the program list once, for the program filter. Prefer the lightweight
  // summary (no embedded lectures); fall back to the full list if unavailable.
  useEffect(() => {
    if (!isProgram || locked) return;
    let cancelled = false;
    const asList = (res: any) => {
      const d = res.data?.data ?? res.data;
      return Array.isArray(d) ? d : d?.items || [];
    };
    (async () => {
      try {
        const list = asList(await axiosInstance.get(APIS.EVENTS.SUMMARY));
        if (!cancelled) setPrograms(list);
      } catch {
        try {
          const list = asList(await axiosInstance.get(APIS.EVENTS.LIST));
          if (!cancelled) setPrograms(list);
        } catch {
          if (!cancelled) setPrograms([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isProgram, locked]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.QUIZ_ATTEMPTS.ALL, {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
          passed: passed || undefined,
          ...scopeParams,
        },
      });
      const data = res.data?.data ?? res.data;
      setRows(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load quiz results', { variant: 'error' });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, passed, scopeParams, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Per-quiz aggregation.
  useEffect(() => {
    axiosInstance
      .get(APIS.QUIZ_ATTEMPTS.STATS, { params: scopeParams })
      .then((res) => setStats((res.data?.data ?? res.data) || []))
      .catch(() => setStats([]));
  }, [scopeParams]);

  // Per-student-per-quiz rollup (final score = average across attempts).
  useEffect(() => {
    axiosInstance
      .get(APIS.QUIZ_ATTEMPTS.BY_STUDENT, { params: scopeParams })
      .then((res) => setByStudent((res.data?.data ?? res.data) || []))
      .catch(() => setByStudent([]));
  }, [scopeParams]);

  const userOf = (r: AttemptRow) => r.userId || r.user || null;
  // Program attempts carry eventId; course attempts carry courseId.
  const sourceOf = (r: AttemptRow) =>
    nameOf(r.eventId || r.event || null) || nameOf(r.courseId || r.course || null) || '—';

  // Weighted rollup across the per-quiz stats (attempts-weighted, not a naive mean).
  const summary = useMemo(() => {
    const attempts = stats.reduce((n, s) => n + (s.attempts || 0), 0);
    if (!attempts) return null;
    const passedCount = stats.reduce((n, s) => n + (s.passedCount || 0), 0);
    const weighted = stats.reduce((n, s) => n + (s.avgPercentage || 0) * (s.attempts || 0), 0);
    return {
      quizzes: stats.length,
      attempts,
      avg: Math.round(weighted / attempts),
      passRate: Math.round((passedCount / attempts) * 100),
    };
  }, [stats]);

  const emptyMsg = isProgram
    ? 'No program quiz attempts yet. Attempts appear here once students submit a program lecture quiz.'
    : 'No quiz attempts yet.';

  const body = (
    <>
      {!embedded && (
        <HeaderBreadcrumbs
          heading={heading}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: heading }]}
        />
      )}

      {isProgram && !locked && (
          <Card sx={{ p: 2.5, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                select
                size="small"
                label="Program"
                value={eventId}
                onChange={(e) => {
                  setEventId(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 260 }}
              >
                <MenuItem value="">All programs</MenuItem>
                {programs.map((p: any) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.title || p.name}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                Results from per-lecture quizzes inside programs. Pick a program to narrow every
                panel below.
              </Typography>
            </Stack>
          </Card>
        )}

        {/* When embedded (the per-batch Quiz Report), the parent page already
            renders these four KPIs for the cohort, so hide this strip to avoid
            a duplicate — same `!embedded` gate as the header above. */}
        {!embedded && summary && (
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Kpi label="Quizzes" value={summary.quizzes} />
            <Kpi label="Attempts" value={summary.attempts} />
            <Kpi label="Average score" value={`${summary.avg}%`} />
            <Kpi label="Pass rate" value={`${summary.passRate}%`} />
          </Stack>
        )}

        {stats.length > 0 && (
          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Per-quiz summary
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quiz</TableCell>
                    <TableCell>{sourceLabel}</TableCell>
                    <TableCell align="center">Attempts</TableCell>
                    <TableCell align="center">Students</TableCell>
                    <TableCell align="center">Avg %</TableCell>
                    <TableCell align="center">Pass rate</TableCell>
                    <TableCell align="center">Avg time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.map((s) => {
                    const key = `${s.quizId}|${s.courseId || ''}|${s.eventId || ''}`;
                    return (
                      <Fragment key={key}>
                        <TableRow hover>
                          <TableCell>{s.quizTitle || s.quizId}</TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <span>{s.sourceName || s.programName || s.courseName || '—'}</span>
                              {!scope && s.sourceType === 'program' && (
                                <Label color="info">Program</Label>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">{s.attempts}</TableCell>
                          <TableCell align="center">{s.students}</TableCell>
                          <TableCell align="center">{s.avgPercentage}%</TableCell>
                          <TableCell align="center">
                            {s.avgDurationSeconds ? (
                              <Tooltip
                                title={`Average of ${s.timedAttempts || 0} timed attempt(s); longest ${fmtDuration(
                                  s.maxDurationSeconds
                                )}`}
                              >
                                <span>{fmtDuration(s.avgDurationSeconds)}</span>
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {byStudent.length > 0 && (
          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              Per-student scores
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Final quiz score = the average across all of a student&apos;s attempts.
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>{sourceLabel}</TableCell>
                    <TableCell>Quiz</TableCell>
                    <TableCell align="center">Attempts</TableCell>
                    <TableCell align="center">Average %</TableCell>
                    <TableCell align="center">Best %</TableCell>
                    <TableCell align="center">Ever passed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byStudent.map((s, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="subtitle2" noWrap>
                            {s.studentName || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {s.studentEmail || ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {s.sourceName || s.programName || s.courseName || '—'}
                      </TableCell>
                      <TableCell>{s.quizTitle || s.quizId}</TableCell>
                      <TableCell align="center">{s.attempts}</TableCell>
                      <TableCell align="center">
                        <b>{s.avgPercentage}%</b>
                      </TableCell>
                      <TableCell align="center">{s.bestPercentage}%</TableCell>
                      <TableCell align="center">
                        <Label color={s.passed ? 'success' : 'error'}>
                          {s.passed ? 'Yes' : 'No'}
                        </Label>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        <Card>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
            <TextField
              select
              size="small"
              label="Result"
              value={passed}
              onChange={(e) => {
                setPassed(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 200 }}
            >
              {PASS_FILTERS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <ColumnsButton
              columns={columns}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </Stack>

          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
              />
            )}
            <Table size="medium" sx={{ minWidth: 880 }}>
              <TableHead>
                <TableRow>
                  {isVisible('date') && <TableCell>Date</TableCell>}
                  {isVisible('student') && <TableCell>Student</TableCell>}
                  {isVisible('source') && <TableCell>{sourceLabel}</TableCell>}
                  {isVisible('quiz') && <TableCell>Quiz</TableCell>}
                  {isVisible('score') && <TableCell align="center">Score</TableCell>}
                  {isVisible('percentage') && <TableCell align="center">%</TableCell>}
                  {isVisible('result') && <TableCell align="center">Result</TableCell>}
                  {isVisible('attempt') && <TableCell align="center">Attempt</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length || 1} align="center" sx={{ py: 4 }}>
                      <Box sx={{ color: 'text.secondary' }}>{emptyMsg}</Box>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => {
                  const u = userOf(r);
                  const isProgramRow = !!(r.eventId || r.event);
                  return (
                    <TableRow key={r._id} hover>
                      {isVisible('date') && (
                        <TableCell>{safeDate(r.submittedAt || r.createdAt)}</TableCell>
                      )}
                      {isVisible('student') && (
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="subtitle2" noWrap>
                              {u?.name || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {u?.email || ''}
                            </Typography>
                          </Stack>
                        </TableCell>
                      )}
                      {isVisible('source') && (
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <span>{sourceOf(r)}</span>
                            {!scope && isProgramRow && <Label color="info">Program</Label>}
                          </Stack>
                        </TableCell>
                      )}
                      {isVisible('quiz') && <TableCell>{r.quizTitle || r.quizId || '—'}</TableCell>}
                      {isVisible('score') && (
                        <TableCell align="center">
                          {r.score ?? 0} / {r.maxScore ?? 0}
                        </TableCell>
                      )}
                      {isVisible('percentage') && (
                        <TableCell align="center">{r.percentage ?? 0}%</TableCell>
                      )}
                      {isVisible('result') && (
                        <TableCell align="center">
                          <Label color={r.passed ? 'success' : 'error'}>
                            {r.passed ? 'Passed' : 'Failed'}
                          </Label>
                        </TableCell>
                      )}
                      {isVisible('attempt') && (
                        <TableCell align="center">{r.attemptNumber ?? 1}</TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Card>
    </>
  );

  // Embedded (inside the per-batch report page) renders bare, so the host page
  // owns the Page/Container/breadcrumbs.
  if (embedded) return body;

  return (
    <Page title={heading}>
      <Container maxWidth={themeStretch ? false : 'lg'}>{body}</Container>
    </Page>
  );
}
